import { Component, inject } from '@angular/core';
import { CommonModule, JsonPipe } from '@angular/common';
import { FormGroup, Validators } from '@angular/forms';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheetRef,
  MatBottomSheetModule,
} from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { FormlyModule, FormlyFieldConfig } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { RecipientCorrectionRequest, RecipientEditSheetData, RecipientEditSheetResult } from '@lmc-app/summary/docusign/interface/docusign-envelope-recipients-interface';
import { DocusignService } from '@lmc-app/core/services/docusign.service';
import { ENVELOPE_ROLES, RecipientListSettings } from '@lmc-app/summary/docusign/constants/docusign-constants';
import dayjs from 'dayjs';

@Component({
  selector: 'app-correct-recipient-delivery',
  templateUrl: './correct-recipient-delivery.component.html',
  styleUrls: ['./correct-recipient-delivery.component.scss'],
  imports: [
    JsonPipe,
    CommonModule,
    MatBottomSheetModule,
    MatButtonModule,
    FormlyModule,
    FormlyMaterialModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
  ],
  standalone: true
})
export class CorrectRecipientDeliveryComponent {        
  form = new FormGroup({});
  fields: FormlyFieldConfig[];
  model: {
    email: string;
    addPhone: boolean;
    countryCode: string;
    phoneNumber: string;
    phoneMode: 'additional' | 'full';
  };
  saving = false;
  error = '';
  private hasExistingPhone: boolean;
   
  public data: RecipientEditSheetData = inject(MAT_BOTTOM_SHEET_DATA, { optional: true }); 	// Correction Data
  constructor(
    private _docusignService: DocusignService,
    private _bottomSheetRef: MatBottomSheetRef<CorrectRecipientDeliveryComponent,RecipientEditSheetResult>
  ) {
    const r = this.data.recipient;    
    // // const hasExistingPhone = !!r.phoneNumber;

    const existingSmsNotification = r.additionalNotifications?.find(
      (n) => n.secondaryDeliveryMethod === 'SMS'
    );
    const existingPhone = r.phoneNumber || existingSmsNotification?.phoneNumber;
    const hasExistingPhone = !!existingPhone;
    this.hasExistingPhone = hasExistingPhone;

    // // this.model = {
    // //   email: r.email || '',
    // //   addPhone: false,
    // //   countryCode: '1',
    // //   phoneNumber: r.phoneNumber?.number || '',
    // //   phoneMode: 'additional',
    // // };

    this.model = {
      email: r.email || '',
      addPhone: false,
      countryCode: existingPhone?.countryCode || '1',
      phoneNumber: existingPhone?.number || '',
      // Keep the current mode: SMS-only delivery is 'full', Email + SMS reminder is 'additional'
      phoneMode: (r.deliveryMethod || '').toLowerCase() === 'sms' ? 'full' : 'additional',
    };

    this.fields = [
      {
        key: 'email',
        type: 'input',
        props: {
          label: 'Email',
          type: 'email',
          placeholder: 'recipient@example.com',          
          appearance: 'outline', 
          floatLabel: 'always',
        },
        validators: {
          validation: [Validators.email],
        },
      },
      ...(!hasExistingPhone
        ? [
            {
              key: 'addPhone',
              type: 'checkbox',
              wrappers: [],
              props: {
                label: 'Add phone number (SMS)',
                appearance: 'outline', 
                floatLabel: 'always',
              },
            } as FormlyFieldConfig,
          ]
        : []),
      {
        key: 'phoneNumber',
        type: 'input',
        props: {
          label: 'Phone number',
          placeholder: '2125551212',
          appearance: 'outline', 
          floatLabel: 'always',
        },
        expressions: {
          hide: (field) => !hasExistingPhone && !field.model?.addPhone,
        },
        validators: {
          validation: [Validators.required, Validators.pattern(/^\d{7,15}$/)],
        },
      },
      // // {
      // //   key: 'phoneNumber',
      // //   type: 'input',
      // //   props: {
      // //     label: 'Phone number',
      // //     placeholder: '1234567891',
      // //     appearance: 'outline', 
      // //     floatLabel: 'always',
      // //   },
      // //   expressions: {
      // //     hide: (field) => !hasExistingPhone && !field.model?.addPhone,
      // //   },
      // //   validators: {
      // //     validation: [Validators.required, Validators.pattern(/^\d{7,15}$/)],
      // //   },
      // // },
      // {
      //   key: 'phoneMode',
      //   type: 'select',
      //   defaultValue: 'additional',
      //   props: {
      //     label: 'Delivery mode',
      //     options: [
      //       { value: 'additional', label: 'SMS as a reminder only' },
      //       { value: 'full', label: 'Recipient can sign via SMS link' },
      //     ],
      //     appearance: 'outline', 
      //     floatLabel: 'always',
      //   },
      //   expressions: {
      //     hide: (field) => !hasExistingPhone && !field.model?.addPhone,
      //   },
      // },
    ];
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.error = '';

    const { email, addPhone, countryCode, phoneNumber, phoneMode } = this.model;
    const r = this.data.recipient;

    const payload: RecipientCorrectionRequest = {
      recipientId: r.recipientId,
      name: r.name,
      roleName: r.roleName,
    };

    if (email) {
      payload.email = email;
    }
    // Existing SMS can live on r.phoneNumber (SMS delivery) or in additionalNotifications (Email + SMS),
    // so always resend it to keep the recipient's current delivery channels
    if ((addPhone || this.hasExistingPhone) && countryCode && phoneNumber) {
      payload.phoneNumber = { countryCode, number: phoneNumber };
      payload.phoneMode = phoneMode;
    }

    this._docusignService.correctRecipient(this.data.envelopeId, payload).subscribe({
      next: () => {
        this.correctedResult = {
          email: payload.email,
          phoneNumber: payload.phoneNumber,
        };
        this.saveZohoChanges(payload);
      },
      error: (err) => {
        this.saving = false;
        this.error = err?.error?.error || 'Failed to update recipient.';
      },
    });
  }

  onCancel(): void {
    // Still refresh the status list if DocuSign was already corrected (e.g. only the Zoho sync failed)
    this._bottomSheetRef.dismiss(this.correctedResult);
  }

  // ---------- Zoho sync ----------
  private correctedResult: RecipientEditSheetResult | undefined;

  // Sync the corrected email / SMS phone to Zoho for the matching Client or Co-Applicant
  private saveZohoChanges(payload: RecipientCorrectionRequest): void {
    const appData = this.data?.appData;
    const isClient = payload.roleName === ENVELOPE_ROLES.CLIENT;
    const isCoApplicant = payload.roleName === ENVELOPE_ROLES.COAPPLICANT;

    // Only the Primary (Client) and Co-Applicant contact info lives in Zoho
    if (!isClient && !isCoApplicant) {
      this.saving = false;
      this._bottomSheetRef.dismiss(this.correctedResult);
      return;
    }
    
    // Client -> primary applicant fields (Email / Mobile), Co-Applicant -> CoAppEmail / CoAppMobile
    const source = RecipientListSettings.CONTACT_SOURCE_FIELDS[isClient ? 'CL' : 'CO'];
    const emailField = appData?.[source.email];
    const mobileField = appData?.[source.mobile];

    const zohoUpdateData: Record<string, string> = {};
    // Empty email is never sent, so a cleared email does not overwrite Zoho
    if (payload.email && emailField?.field && payload.email !== emailField.value) {
      zohoUpdateData[emailField.field] = payload.email;
    }
    const phone = payload.phoneNumber?.number;
    if (phone && mobileField?.field && phone !== mobileField.value) {
      zohoUpdateData[mobileField.field] = phone;
    }
    
    if (!Object.keys(zohoUpdateData).length) {
      this.saving = false;
      this._bottomSheetRef.dismiss(this.correctedResult);
      return;
    }

    // Contact record belongs to the primary applicant; Co-Applicant fields live on the Deal only
    const contactId = isClient ? appData?.ContactId?.value : null;
    const zohoPayload = {
      ...zohoUpdateData,
      Override_Type: `${this.data?.currentUser?.full_name} (${dayjs().format('YYYY-MM-DD HH:mm:ss')})`,
    };

    this._docusignService.saveDocusignZohoChanges(appData?.ApplicationId?.value, zohoPayload, contactId).subscribe({
      next: () => {
        // Keep the local agreement data in sync so later edits compare against the new values
        if (emailField?.field && zohoUpdateData[emailField.field]) emailField.value = payload.email;
        if (mobileField?.field && zohoUpdateData[mobileField.field]) mobileField.value = phone;
        this.saving = false;
        this._bottomSheetRef.dismiss(this.correctedResult);
      },
      error: () => {
        this.saving = false;
        this.error = 'Recipient updated in DocuSign, but failed to update Zoho.';
      },
    });
  }
}